import { useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLanguage } from "@/lib/LanguageContext";

export function useFileUpload() {
  const [file, setFile] = useState(null);
  const [domains, setDomains] = useState([]);
  const [fileId, setFileId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { t } = useLanguage();

  const uploadFile = async (fileToUpload) => {
    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("file", fileToUpload);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const response = await api.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      setDomains(response.data.domains);
      setFileId(response.data.id);
      toast.success(t("toast.domainsFound", { count: response.data.domain_count }));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || t("toast.uploadFailed");
      toast.error(errorMessage);
      setFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const validateAndSetFile = async (selectedFile) => {
    const extension = selectedFile.name.split(".").pop().toLowerCase();
    const validExtensions = ["csv", "xls", "xlsx"];

    if (!validExtensions.includes(extension)) {
      toast.error(t("toast.invalidFile"));
      return;
    }

    setFile(selectedFile);
    await uploadFile(selectedFile);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setDomains([]);
    setFileId(null);
    setUploadProgress(0);
  };

  const removeFile = () => {
    setFile(null);
    setDomains([]);
    setFileId(null);
    setUploadProgress(0);
  };

  return {
    file,
    domains,
    fileId,
    isUploading,
    isDragOver,
    uploadProgress,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileSelect,
    resetUpload,
    removeFile,
  };
}
